import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_TOOLBOX_PERSONA,
  TOOLBOX_PERSONAS,
  type CreateRoleAssignmentBody,
  type ToolboxCapability,
  type ToolboxPersona,
} from "@ttc-spectree/toolbox-contract/roles";
import { toolboxAccessApi, type ToolboxAccessResponse, type ToolboxProjectAccess } from "@/lib/api/toolbox-access";
import type { Me } from "@/lib/api/me";
import { meKeys } from "@/hooks/queries/use-me";

/**
 * TanStack Query hooks for SpecTree Toolbox access (epic PI-SPEC-SW-E283,
 * feature PI-SPEC-SW-1429). Pages hide exactly what the SpecTree API would
 * refuse, from the capabilities in `GET /toolbox/access`; they never branch
 * on the Toolbox bridge for access, so the browser and the app agree.
 */
export const toolboxAccessKeys = {
  all: ["toolbox-access"] as const,
  access: () => [...toolboxAccessKeys.all, "current"] as const,
  granters: (project?: string) => [...toolboxAccessKeys.all, "granters", project?.toLowerCase() ?? ""] as const,
  roles: () => [...toolboxAccessKeys.all, "roles"] as const,
  projectRoles: (project: string) => [...toolboxAccessKeys.roles(), project.toLowerCase()] as const,
};

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests and for pages that already hold the data)
// ---------------------------------------------------------------------------

/** The caller's access entry for `project` (case-insensitive), or null when they hold no role there. */
export function projectAccessOf(
  access: ToolboxAccessResponse | null | undefined,
  project: string | null | undefined
): ToolboxProjectAccess | null {
  if (!access || !project) return null;
  const wanted = project.toLowerCase();
  return access.projects.find((entry) => entry.container.toLowerCase() === wanted) ?? null;
}

/** True when the caller may use `capability` on `project`. An Admin may use every capability everywhere. */
export function canOn(
  access: ToolboxAccessResponse | null | undefined,
  project: string | null | undefined,
  capability: ToolboxCapability
): boolean {
  if (!access) return false;
  if (access.isAdmin) return true;
  return projectAccessOf(access, project)?.capabilities.includes(capability) ?? false;
}

/** True when the caller holds `capability` on at least one project (or is an Admin). */
export function canAnywhere(access: ToolboxAccessResponse | null | undefined, capability: ToolboxCapability): boolean {
  if (!access) return false;
  if (access.isAdmin) return true;
  return access.projects.some((entry) => entry.capabilities.includes(capability));
}

/** The projects on which the caller holds `capability`, as listed by the API. */
export function projectsWith(
  access: ToolboxAccessResponse | null | undefined,
  capability: ToolboxCapability
): string[] {
  if (!access) return [];
  return access.projects.filter((entry) => entry.capabilities.includes(capability)).map((entry) => entry.container);
}

/** True when the caller holds no role anywhere and is not an Admin. */
export function hasNoRole(access: ToolboxAccessResponse | null | undefined): boolean {
  if (!access) return false;
  return !access.isAdmin && access.projects.every((entry) => entry.roles.length === 0);
}

/**
 * The personas the caller may choose on `project` and the one in use. An
 * Admin on a project they hold no stored role on may choose any persona.
 */
export function personasOn(
  access: ToolboxAccessResponse | null | undefined,
  project: string | null | undefined
): { personas: ToolboxPersona[]; persona: ToolboxPersona | null } {
  const entry = projectAccessOf(access, project);
  if (entry) return { personas: entry.personas, persona: entry.persona ?? entry.personas[0] ?? null };
  if (access?.isAdmin) return { personas: [...TOOLBOX_PERSONAS], persona: DEFAULT_TOOLBOX_PERSONA };
  return { personas: [], persona: null };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * The caller's Toolbox access (roles, capabilities and personas per project).
 * `refetchInterval` re-reads it while the caller is mounted (Setup's "Ask for
 * a role" checks every minute for a role to land).
 */
export function useToolboxAccess(options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: toolboxAccessKeys.access(),
    queryFn: () => toolboxAccessApi.get(),
    staleTime: 60_000,
    ...(options.refetchInterval ? { refetchInterval: options.refetchInterval } : {}),
  });
}

/**
 * Keep `/me` in step with the Toolbox access read. `/me`'s `groomingVisible`
 * decides RequireGroomingVisible and the title bar's Grooming controls, while
 * Setup's "Ask for a role" polls (and "Check again" re-reads) only
 * `GET /toolbox/access`. Whenever that read comes back with a role while the
 * cached `/me` still says no Toolbox pages, `/me` is read again, so a role
 * that lands opens Grooming without a reload.
 */
export function useMeFollowsToolboxAccess(): void {
  const queryClient = useQueryClient();
  const { data: access, dataUpdatedAt } = useToolboxAccess();
  useEffect(() => {
    if (!access || hasNoRole(access)) return;
    const me = queryClient.getQueryData<{ data: Me }>(meKeys.me());
    if (me && !me.data.groomingVisible) void queryClient.invalidateQueries({ queryKey: meKeys.me() });
  }, [access, dataUpdatedAt, queryClient]);
}

/** Who can grant a Toolbox role (Admins; with `project`, its Project Managers too). */
export function useRoleGranters(project?: string) {
  return useQuery({
    queryKey: toolboxAccessKeys.granters(project),
    queryFn: () => toolboxAccessApi.getRoleGranters(project),
    staleTime: 5 * 60_000,
  });
}

/**
 * Whether the caller may use `capability` on `project`: `allowed` is false
 * while the access read is loading or failed, so a control appears only once
 * the API has said yes.
 */
export function useCan(
  project: string | null | undefined,
  capability: ToolboxCapability
): { allowed: boolean; isLoading: boolean } {
  const { data, isLoading } = useToolboxAccess();
  return { allowed: canOn(data, project, capability), isLoading };
}

/** Whether the caller holds `capability` on any project (home tiles, setup checks). */
export function useCanAnywhere(capability: ToolboxCapability): { allowed: boolean; isLoading: boolean } {
  const { data, isLoading } = useToolboxAccess();
  return { allowed: canAnywhere(data, capability), isLoading };
}

/** The personas the caller may choose on `project` and the one currently in use. */
export function useProjectPersonas(project: string | null | undefined): {
  personas: ToolboxPersona[];
  persona: ToolboxPersona | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useToolboxAccess();
  return { ...personasOn(data, project), isLoading };
}

/** Choose the caller's persona on one project; refreshes the access read. */
export function useSetPersona() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ project, persona }: { project: string; persona: ToolboxPersona }) =>
      toolboxAccessApi.setPersona(project, persona),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: toolboxAccessKeys.access() });
    },
  });
}

/** Role assignments on one project (Admins and that project's Project Managers). */
export function useProjectRoles(project: string | null | undefined) {
  return useQuery({
    queryKey: toolboxAccessKeys.projectRoles(project ?? ""),
    queryFn: () => toolboxAccessApi.listRoles(project ?? ""),
    enabled: Boolean(project),
  });
}

/**
 * After a role change: the access read and role lists, and the role holders'
 * time zones the Meeting schedule offers first (`groomingKeys.teamTimeZones`,
 * matched by its last key part to keep this module free of the grooming hooks).
 */
async function invalidateAfterRoleChange(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: toolboxAccessKeys.all }),
    queryClient.invalidateQueries({ predicate: (query) => query.queryKey.at(-1) === "team-time-zones" }),
  ]);
}

/** Assign a per-project role; refreshes the access read, the role lists and the team time zones. */
export function useAssignRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoleAssignmentBody) => toolboxAccessApi.assignRole(body),
    onSuccess: async () => {
      await invalidateAfterRoleChange(queryClient);
    },
  });
}

/** Remove a role assignment; refreshes the access read, the role lists and the team time zones. */
export function useRemoveRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => toolboxAccessApi.removeRole(assignmentId),
    onSuccess: async () => {
      await invalidateAfterRoleChange(queryClient);
    },
  });
}
